import { memo, useCallback, useState, useEffect } from 'react';
import { View, Pressable, Text, Image, StyleSheet } from 'react-native';
import { COLOR, FONT, __WEB__ } from '../../constants/styles';

const SUB_ITEM_HEIGHT = __WEB__ ? 50 : 65;
const MAIN_ITEM_HEIGHT = __WEB__ ? 55 : 65;
const SUB_ITEM_VERTICAL_MARGIN = __WEB__ ? 5 : 5;

const MainNavItemSingle = memo((props) => {
  const [hovered, setHovered] = useState(false);
  const handleHoverIn = useCallback(() => {
    if (__WEB__) {
      setHovered(true);
    }
  }, []);
  const handleHoverOut = useCallback(() => {
    if (__WEB__) {
      setHovered(false);
    }
  }, []);
  const isSubItem = props.styleType === 'sub';
  const isSelected = props.selected;
  const handlePress = useCallback(() => {
    props.onPress?.(props.screenConfig);
  }, [props.onPress, props.screenConfig]);
  const itemStyles = isSubItem ? styles.subItem : {};
  const itemSelectedStyles = !isSelected
    ? {}
    : isSubItem
      ? styles.subItemSelected
      : styles.mainNavItemSelected;
  const iconStyles = isSubItem ? styles.subItemIcon : {};
  const iconSelectedStyles = !isSelected
    ? {}
    : isSubItem
      ? styles.subItemIconSelected
      : styles.mainNavItemIconSelected;
  const labelStyles = isSubItem ? styles.subItemLabel : {};
  const labelSelectedStyles = !isSelected
    ? {}
    : isSubItem
      ? styles.subItemLabelSelected
      : styles.mainNavItemLabelSelected;
  return (
    <Pressable
      style={[
        styles.mainNavItem,
        itemStyles,
        itemSelectedStyles,
        hovered ? styles.hoveredItem : null,
        props.style,
      ]}
      onPress={handlePress}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
    >
      <Image
        style={[styles.mainNavItemIcon, iconStyles, iconSelectedStyles, props.iconStyle]}
        source={(isSelected ? props.iconExpanded : props.icon) || props.icon}
      />
      <Text style={[styles.mainNavItemLabel, labelStyles, labelSelectedStyles]}>{props.label}</Text>
    </Pressable>
  );
});

export const MainNavItem = memo((props) => {
  const [expanded, setExpanded] = useState(props.defaultExpanded || false);
  const [hovered, setHovered] = useState(false);
  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);
  const handleHoverIn = useCallback(() => {
    if (__WEB__) {
      setHovered(true);
    }
  }, []);
  const handleHoverOut = useCallback(() => {
    if (__WEB__) {
      setHovered(false);
    }
  }, []);

  useEffect(() => {
    setExpanded(!!props.selected);
  }, [props.selected]);

  if (!props.subItems?.length) {
    return (
      <View style={styles.navItemSingleContainer}>
        <MainNavItemSingle {...props} />
      </View>
    );
  }

  return (
    <View style={styles.navItemNestedContainer}>
      <Pressable
        style={[styles.expandableNavItem, hovered ? styles.hoveredItem : null]}
        onPress={toggleExpanded}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
      >
        <Image
          style={[styles.expandIcon, !expanded && { transform: [{ rotate: '-90deg' }] }]}
          source={require('../../../resources/arrow-down.png')}
        />
        <View style={[styles.mainNavItem]}>
          <Image
            style={[styles.mainNavItemIcon]}
            source={(expanded ? props.iconExpanded : props.icon) || props.icon}
          />
          <Text
            style={[styles.mainNavItemLabel, expanded ? styles.mainNavItemLabelSelected : null]}
          >
            {props.label}
          </Text>
        </View>
      </Pressable>
      <View
        style={[
          styles.expandableContainer,
          {
            marginLeft: 0,
            height: expanded
              ? props.subItems.length * SUB_ITEM_HEIGHT +
                props.subItems.length * SUB_ITEM_VERTICAL_MARGIN +
                MAIN_ITEM_HEIGHT +
                35
              : 0,
            overflow: 'hidden',
          },
        ]}
      >
        {props.subItems.map((subItem) => (
          <MainNavItemSingle
            key={subItem.label}
            styleType="sub"
            {...subItem}
            onPress={props.onPress}
          />
        ))}
      </View>
    </View>
  );
});

export const BottomNavItem = memo((props) => {
  return null;
});

const styles = StyleSheet.create({
  expandableNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 5,
    borderRadius: 12,
    marginVertical: 3,
    height: MAIN_ITEM_HEIGHT - 6,
  },
  expandIcon: {
    width: 12,
    height: 12,
    marginLeft: 8,
    marginRight: 0,
    tintColor: 'white',
    transition: __WEB__ ? 'transform 150ms ease-out' : undefined,
    transform: [{ rotate: '0deg' }],
  },
  navItemNestedContainer: {
    marginHorizontal: 20,
    backgroundColor: 'rgb(18, 26, 43)',
    borderColor: 'rgb(65, 72, 85)',
    borderWidth: 1,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 5,
    marginBottom: 5,
  },
  expandableContainer: {
    transition: __WEB__ ? 'height 150ms ease-out' : undefined,
    height: 0,
  },
  navItemSingleContainer: {
    marginHorizontal: 20,
  },
  mainNavItem: {
    flexDirection: 'row',
    alignItems: 'center',
    height: MAIN_ITEM_HEIGHT - 6,
    paddingHorizontal: 6,
    borderRadius: 12,
    marginVertical: 3,
  },
  subItem: {
    height: SUB_ITEM_HEIGHT,
    paddingHorizontal: __WEB__ ? 15 : 15,
    paddingVertical: __WEB__ ? 15 : 15,
    marginVertical: SUB_ITEM_VERTICAL_MARGIN,
    paddingHorizontal: 0,
    marginHorizontal: 5,
  },
  subItemSelected: {
    backgroundColor: COLOR.emphasis,
    borderRadius: 12,
  },
  mainNavItemIcon: {
    width: 20,
    height: 20,
    marginRight: 15,
    marginLeft: 5,
    tintColor: 'white',
  },
  mainNavItemLabel: {
    ...FONT.regular,
    fontSize: __WEB__ ? 16 : 20,
    color: 'white',
  },
  mainNavItemLabelSelected: {
    ...FONT.bold,
  },
  subItemLabel: {
    ...FONT.regular,
  },
  subItemLabelSelected: {
    ...FONT.bold,
    color: 'rgb(18, 26, 43)',
  },
  subItemIcon: {
    marginRight: 10,
    marginLeft: 15,
  },
  subItemIconSelected: {
    tintColor: 'rgb(18, 26, 43)',
  },
  hoveredItem: {
    backgroundColor: 'rgb(52, 81, 127)',
  },
});
